import React, { createContext, useState, useContext, useEffect } from 'react';
import './styles/App.css';
import Header from './components/layout/Header/Header';
import Footer from './components/layout/Footer/Footer';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { createContext as createCtx } from 'react';
import { getCatalogSettings } from './services/settingsCache';

import Home from './pages/Home/Home';
import Products from './pages/Products/Products';
import Services from './pages/Services/Services';
import CustomSolutions from './pages/CustomSolutions/CustomSolutions';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import FAQ from './pages/FAQ/FAQ';
import ProductCategory from './pages/Products/ProductCategory';
import ProductDetail from './pages/Products/ProductDetail';
import Login from './pages/auth/Login';
import Cart from './pages/Cart/Cart';
import CallbackRequest from './pages/CallbackRequest/CallbackRequest';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse/TermsOfUse';
import BackendTest from './components/BackendTest';
import OfferPopup from './components/common/OfferPopup/OfferPopup';

const SKIP_AUTH = true;
const SKIP_THEME = true;

// Create a Theme Context
const ThemeContext = createContext();

// Create a Cart Context
const CartContext = createCtx();
const CatalogSettingsContext = createCtx({ showProductRate: false, isCatalogSettingsLoading: true });

// Custom hook to use CartContext
export const useCart = () => useContext(CartContext);
export const useCatalogSettings = () => useContext(CatalogSettingsContext);

// Create a custom hook to use the ThemeContext
export const useTheme = () => useContext(ThemeContext);

function App() {
  const location = useLocation();
  const [isNightModeState, setIsNightModeState] = useState(() => {
    if (SKIP_THEME) return false;
    if (typeof window === 'undefined') return false;
    try {
      const stored = localStorage.getItem('theme');
      if (stored === 'night') return true;
      if (stored === 'day') return false;
      // Fallback to system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return true;
      }
    } catch (e) {
      // ignore read errors and fall back to default
    }
    return false;
  });
  const isNightMode = SKIP_THEME ? false : isNightModeState;
  const setIsNightMode = SKIP_THEME ? () => {} : setIsNightModeState;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [catalogSettings, setCatalogSettings] = useState({ showProductRate: false, isCatalogSettingsLoading: true });
  // Cart state and helpers (declare hooks unconditionally at top)
  const [cartItems, setCartItems] = useState([]);
  // Check authentication status
  useEffect(() => {
    if (SKIP_AUTH) {
      setIsAuthenticated(true);
      setIsCheckingAuth(false);
      return;
    }

    const checkAuth = () => {
      const user = localStorage.getItem('user');
      setIsAuthenticated(!!user);
      setIsCheckingAuth(false);
    };

    checkAuth();

    // Listen for storage changes (when user logs in/out in another tab)
    window.addEventListener('storage', checkAuth);

    // Custom event for login/logout
    const handleAuthChange = () => checkAuth();
    window.addEventListener('authChange', handleAuthChange);

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCatalogSettings = async () => {
      try {
        const data = await getCatalogSettings();
        if (!cancelled) {
          setCatalogSettings({
            showProductRate: !!data?.showProductRate,
            isCatalogSettingsLoading: false,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setCatalogSettings({
            showProductRate: false,
            isCatalogSettingsLoading: false,
          });
        }
      }
    };

    loadCatalogSettings();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (SKIP_THEME) {
      document.body.style.background = '#f4f4f4';
      document.body.style.color = '#0f172a';
      return;
    }
    document.body.style.background = isNightMode ? '#020617' : '#f4f4f4';
    document.body.style.color = isNightMode ? '#e5e7eb' : '#0f172a';
  }, [isNightMode]);

  // Persist theme preference
  useEffect(() => {
    if (SKIP_THEME || typeof window === 'undefined') return;
    try {
      localStorage.setItem('theme', isNightMode ? 'night' : 'day');
    } catch (e) {
      // ignore write errors
    }
  }, [isNightMode]);

  // Scroll to top on route change (unless hash is present)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (location.hash) return;
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // Show loading state while checking authentication
  if (!SKIP_AUTH && isCheckingAuth) {
    return (
      <ThemeContext.Provider value={{ isNightMode, setIsNightMode }}>
        <div className={isNightMode ? 'night-mode' : 'day-mode'}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            background: isNightMode ? '#020617' : '#f4f4f4',
            color: isNightMode ? '#e5e7eb' : '#0f172a'
          }}>
            <div>Loading...</div>
          </div>
        </div>
      </ThemeContext.Provider>
    );
  }

  // Public routes that don't show header/footer
  const publicPaths = ['/login', '/signup'];
  const isPublicPath = publicPaths.includes(location.pathname.toLowerCase());
  const shouldShowLayout = (SKIP_AUTH || isAuthenticated) && !isPublicPath;

  const addItem = (item) => {
    setCartItems((items) => {
      const found = items.find((i) => i.id === item.id);
      if (found) {
        return items.map((i) => (i.id === item.id ? { ...i, quantity: i.quantity + (item.quantity || 1) } : i));
      }
      return [...items, { ...item, quantity: item.quantity || 1 }];
    });
  };

  const updateItemQuantity = (id, quantity) => {
    setCartItems((items) => items.map((i) => (i.id === id ? { ...i, quantity: Math.max(1, quantity) } : i)));
  };

  const removeItem = (id) => {
    setCartItems((items) => items.filter((i) => i.id !== id));
  };

  const clearCart = () => setCartItems([]);

  const cartTotal = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const NotFound = () => (
    <main style={{ padding: 40 }}>
      <h2>Page not found</h2>
      <p>The page you requested does not exist.</p>
    </main>
  );

  return (
    <ThemeContext.Provider value={{ isNightMode, setIsNightMode }}>
      <CatalogSettingsContext.Provider value={catalogSettings}>
        <CartContext.Provider value={{ cartItems, addItem, updateItemQuantity, removeItem, clearCart, cartTotal, cartCount }}>
          <div className={isNightMode ? 'night-mode' : 'day-mode'} style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <OfferPopup />
            {shouldShowLayout && <Header />}
              <main style={{ flexGrow: 1 }}>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
                  <Route path="/signup" element={isAuthenticated ? <Navigate to="/" /> : <Login />} />
                  
                  {/* Protected Routes */}
                  <Route path="/" element={isAuthenticated ? <Home /> : <Navigate to="/login" />} />
                  <Route path="/products" element={isAuthenticated ? <Products /> : <Navigate to="/login" />} />
                  <Route path="/services" element={isAuthenticated ? <Services /> : <Navigate to="/login" />} />
                  <Route path="/custom-solutions" element={isAuthenticated ? <CustomSolutions /> : <Navigate to="/login" />} />
                  <Route path="/about" element={isAuthenticated ? <About /> : <Navigate to="/login" />} />
                  <Route path="/contact" element={isAuthenticated ? <Contact /> : <Navigate to="/login" />} />
                  <Route path="/callback" element={isAuthenticated ? <CallbackRequest /> : <Navigate to="/login" />} />
                  <Route path="/faq" element={isAuthenticated ? <FAQ /> : <Navigate to="/login" />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
                  <Route path="/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/terms" element={<Navigate to="/terms-of-use" replace />} />
                  <Route path="/cart" element={isAuthenticated ? <Cart /> : <Navigate to="/login" />} />
                  <Route path="/products/:category" element={isAuthenticated ? <ProductCategory /> : <Navigate to="/login" />} />
                  <Route path="/product/:id" element={isAuthenticated ? <ProductDetail /> : <Navigate to="/login" />} />
                  
                  <Route path="/test-backend" element={<BackendTest />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
            {shouldShowLayout && <Footer />}
          </div>
        </CartContext.Provider>
      </CatalogSettingsContext.Provider>
    </ThemeContext.Provider>
  );
}

export default App;
